import { AC_CARD_STATUSES, type AcCardStatus, type MeResponse } from "@seagames/contracts";
import { Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { type CSSProperties, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { gamesOptionsOptions } from "../../lib/competition/games";
import { mainFunctionOptionsOptions, subFunctionOptionsOptions } from "../../lib/functions/queries";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { readableOrganisations } from "../accreditation/model";
import { CloseIcon, DownIcon, FilterIcon } from "./icons";
import { useMedia } from "./use-media";
import { DASHBOARD_FILTER_KEYS, type DashboardSearch } from "./model";

/**
 * Her filter card (`.dfb`): one select per filter the summary endpoint
 * supports — Games (when there is more than one), Organisation (hidden for a
 * login that reads one), Main function, Sub function, Status. Applied on
 * change, like hers: each change is ONE summary request, never per keystroke.
 * On desktop the card is always open; below 1024px it folds into
 * "Filter dashboard", with the count of filters on.
 *
 * The cascade is the records filter sheet's: a new organisation clears the
 * functions (main functions narrow to its type); a new main function clears
 * the sub function; picking a sub function sets its main function.
 */
export function DashboardFilters({
	session,
	value,
	onChange,
}: {
	session: MeResponse;
	value: DashboardSearch;
	onChange: (patch: Partial<DashboardSearch>) => void;
}) {
	const { t } = useTranslation("dashboard");
	const { t: tAcc } = useTranslation("accreditation");
	const wide = useMedia("(min-width: 64rem)");
	const [userOpen, setUserOpen] = useState(false);
	const open = wide || userOpen;

	const orgScope = readableOrganisations(session);
	const showOrg = orgScope.show || !!value.responsible_organization_id;

	const games = useQuery(gamesOptionsOptions());
	const orgs = useQuery({ ...responsibleOrganizationOptionsOptions(), enabled: showOrg });
	const mains = useQuery(mainFunctionOptionsOptions());
	const subs = useQuery(subFunctionOptionsOptions());
	const showGames = (games.data?.length ?? 0) > 1 || !!value.games_id;

	const org = orgs.data?.find((o) => o.responsible_organization_id === value.responsible_organization_id);
	const orgTypeId = org?.organization_type?.id;
	const mainList = (mains.data ?? []).filter((m) => !orgTypeId || !m.organization_type || m.organization_type.id === orgTypeId);
	const mainName = new Map((mains.data ?? []).map((m) => [m.main_function_id, `${m.main_function_code} · ${m.main_function_name}`]));
	const mainIds = new Set(mainList.map((m) => m.main_function_id));
	const subList = (subs.data ?? []).filter((s) => (value.main_function_id ? s.main_function_id === value.main_function_id : mainIds.has(s.main_function_id)));

	const on = DASHBOARD_FILTER_KEYS.filter((k) => !!value[k]).length;
	const cols = [showGames, showOrg, true, true, true].filter(Boolean).length;

	return (
		<details className="db-filters" open={open} style={{ "--db-cols": cols } as CSSProperties}>
			<summary
				onClick={(e) => {
					e.preventDefault();
					setUserOpen((o) => !o);
				}}
			>
				<FilterIcon />
				<span>{t("filters.toggle")}</span>
				{on > 0 && <span className="db-filters__n">{on}</span>}
				<DownIcon />
			</summary>
			<div className="db-filters__g">
				{showGames && (
					<Field label={t("filters.games")}>
						{(id) => (
							<Pick
								id={id}
								all={t("filters.allGames")}
								options={(games.data ?? []).map((g) => ({ value: g.games_id, label: g.name }))}
								value={value.games_id}
								onChange={(v) => onChange({ games_id: v })}
							/>
						)}
					</Field>
				)}
				{showOrg && (
					<Field label={t("filters.organisation")}>
						{(id) => (
							<Pick
								id={id}
								all={t("filters.allOrganisations")}
								options={(orgs.data ?? [])
									.filter(
										(o) =>
											!orgScope.ids ||
											orgScope.ids.includes(o.responsible_organization_id) ||
											o.responsible_organization_id === value.responsible_organization_id,
									)
									.map((o) => ({ value: o.responsible_organization_id, label: o.name }))}
								value={value.responsible_organization_id}
								onChange={(v) => onChange({ responsible_organization_id: v, main_function_id: undefined, sub_function_id: undefined })}
							/>
						)}
					</Field>
				)}
				<Field label={t("filters.mainFunction")}>
					{(id) => (
						<Pick
							id={id}
							all={t("filters.allMainFunctions")}
							options={mainList.map((m) => ({ value: m.main_function_id, label: `${m.main_function_code} · ${m.main_function_name}` }))}
							value={value.main_function_id}
							onChange={(v) => onChange({ main_function_id: v, sub_function_id: undefined })}
						/>
					)}
				</Field>
				<Field label={t("filters.subFunction")}>
					{(id) => (
						<Pick
							id={id}
							all={t("filters.allSubFunctions")}
							options={subList.map((s) => ({
								value: s.sub_function_id,
								label: s.sub_function_name,
								group: value.main_function_id ? undefined : mainName.get(s.main_function_id),
							}))}
							value={value.sub_function_id}
							onChange={(v) => {
								const main = v ? subs.data?.find((s) => s.sub_function_id === v)?.main_function_id : value.main_function_id;
								onChange({ sub_function_id: v, main_function_id: main });
							}}
						/>
					)}
				</Field>
				<Field label={t("filters.status")}>
					{(id) => (
						<Pick
							id={id}
							all={t("filters.allStatuses")}
							searchable={false}
							options={AC_CARD_STATUSES.map((s) => ({ value: s, label: tAcc(`record.status.${s}`) }))}
							value={value.status}
							onChange={(v) => onChange({ status: v as AcCardStatus | undefined })}
						/>
					)}
				</Field>
			</div>
			{on > 0 && (
				<button
					type="button"
					className="db-filters__x cursor-pointer hover:underline"
					onClick={() => onChange(Object.fromEntries(DASHBOARD_FILTER_KEYS.map((k) => [k, undefined])))}
				>
					<CloseIcon />
					{t("filters.clear")}
				</button>
			)}
		</details>
	);
}

function Field({ label, children }: { label: string; children: (id: string) => ReactNode }) {
	const id = useId();
	return (
		<div className="min-w-0">
			<label className="db-filters__l" htmlFor={id}>
				{label}
			</label>
			{children(id)}
		</div>
	);
}

/** The shared Select with her "All …" first option standing for "not set". */
function Pick({
	id,
	all,
	options,
	value,
	onChange,
	searchable,
}: {
	id: string;
	all: string;
	options: SelectOption[];
	value: string | undefined;
	onChange: (v: string | undefined) => void;
	searchable?: boolean;
}) {
	return (
		<Select
			id={id}
			className="db-sel"
			searchable={searchable ?? true}
			options={[{ value: "", label: all }, ...options]}
			value={value ?? ""}
			onChange={(v) => onChange(v || undefined)}
		/>
	);
}
